import { createApi } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn, EndpointBuilder } from '@reduxjs/toolkit/query'
import type { TablePaginationConfig } from 'antd'

import { HTTP_METHODS } from 'common/constants/httpMethodsConstants'
import type { IOption } from 'common/interfaces/IOption'
import type { IRequestData } from 'common/interfaces/IRequestResponse'
import { UtilService } from 'common/services/utilService'
import { baseQuery } from 'common/store/api/fetchBaseQuery'
import { REDUCERS } from 'common/store/reducerConstants'

import type { IEmrProvider, IEmrProvidersResponse, IPatient } from 'features/Patients/interfaces/IPatient'
import { toEmrProviderOptions } from 'features/Patients/state/api/emrProviders.transform'

const { withPaginationParams } = UtilService

export const patientsApi = createApi({
  reducerPath: REDUCERS.PATIENTS,
  baseQuery: baseQuery(),
  tagTypes: ['Patients'],
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({
    fetchPatients: build.query({
      query: (params: TablePaginationConfig) => ({
        url: withPaginationParams('/patients', params),
      }),
      transformResponse: (response: IRequestData<IPatient[]>): IRequestData<IPatient[]> => ({
        ...response,
      }),
      providesTags: ['Patients'],
    }),

    fetchPatientById: build.query<IPatient, { patientId: number }>({
      query: ({ patientId }) => ({
        url: `/patients/${patientId}`,
      }),
    }),

    fetchEmrProviders: build.query<IOption<number>[], void>({
      query: () => ({
        url: '/patients/emr-providers',
      }),
      transformResponse: toEmrProviderOptions,
    }),

    fetchEmrLinkedProviders: build.query<IEmrProvider[], void>({
      query: () => ({
        url: '/patients/emr-providers',
      }),
      transformResponse: (response: IEmrProvidersResponse) => response?.data ?? [],
    }),

    createPatient: build.mutation({
      query: (patient) => ({
        url: '/patients',
        method: HTTP_METHODS.POST,
        data: patient,
      }),
      invalidatesTags: ['Patients'],
    }),
    deletePatient: build.mutation({
      query: (id) => ({
        url: `/patients/${id}`,
        method: HTTP_METHODS.DELETE,
      }),
      invalidatesTags: ['Patients'],
    }),
    updatePatient: build.mutation({
      query: ({ id, patient }) => ({
        url: `/patients/${id}`,
        method: HTTP_METHODS.PUT,
        data: patient,
      }),
      invalidatesTags: ['Patients'],
    }),
  }),
})

export const {
  useLazyFetchPatientsQuery,
  useDeletePatientMutation,
  useCreatePatientMutation,
  useUpdatePatientMutation,
}: any = patientsApi

export const { useLazyFetchPatientByIdQuery, useFetchEmrProvidersQuery, useFetchEmrLinkedProvidersQuery } =
  patientsApi
